import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, TextField, Switch, FormControlLabel,
  Tabs, Tab, Grid, Card, CardContent, Divider, List, ListItem,
  ListItemText, ListItemIcon, Alert, Snackbar, InputAdornment
} from '@mui/material';
import { Security, LockPerson, History, Backup, Search, CheckCircle, Warning, Error, Info, Download, Shield } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

const mockAccessLogs = [
  { id: 1, user: 'Admin User', email: 'admin@era.edu', action: 'Login', ip: '192.168.1.101', time: '2024-01-18 09:14 AM', status: 'success' },
  { id: 2, user: 'Mrs. Smith', email: 'smith@era.edu', action: 'Login', ip: '192.168.1.45', time: '2024-01-18 08:52 AM', status: 'success' },
  { id: 3, user: 'Unknown', email: 'hacker@test.com', action: 'Failed Login', ip: '203.0.113.47', time: '2024-01-18 08:30 AM', status: 'failed' },
  { id: 4, user: 'Admin User', email: 'admin@era.edu', action: 'User Role Modified', ip: '192.168.1.101', time: '2024-01-17 04:22 PM', status: 'success' },
  { id: 5, user: 'Principal Johnson', email: 'principal@era.edu', action: 'Report Downloaded', ip: '192.168.1.20', time: '2024-01-17 03:15 PM', status: 'success' },
  { id: 6, user: 'Unknown', email: 'test@test.com', action: 'Failed Login', ip: '198.51.100.22', time: '2024-01-17 02:10 PM', status: 'failed' },
  { id: 7, user: 'Admin User', email: 'admin@era.edu', action: 'Backup Initiated', ip: '192.168.1.101', time: '2024-01-17 02:00 AM', status: 'success' },
  { id: 8, user: 'Mr. Davis', email: 'davis@era.edu', action: 'Login', ip: '192.168.1.67', time: '2024-01-16 08:45 AM', status: 'success' },
  { id: 9, user: 'Unknown', email: 'attacker@example.com', action: 'Failed Login', ip: '185.176.27.100', time: '2024-01-15 11:35 PM', status: 'failed' },
  { id: 10, user: 'Admin User', email: 'admin@era.edu', action: 'Form Config Updated', ip: '192.168.1.101', time: '2024-01-15 11:00 AM', status: 'success' },
];

const securityAlerts = [
  { severity: 'error', title: '3 Failed Login Attempts', body: 'Multiple failed logins detected from IP 203.0.113.47. Consider blocking this IP.', time: '2 hours ago' },
  { severity: 'warning', title: 'Password Policy', body: '12 user accounts have not updated their passwords in over 90 days.', time: '1 day ago' },
  { severity: 'info', title: 'Security Update Available', body: 'A new security patch is available for your application. Version 1.0.1.', time: '3 days ago' },
];

const SecurityAdmin = () => {
  const location = useLocation();
  const getInitialTab = () => {
    if (location.pathname.includes('/logs')) return 1;
    if (location.pathname.includes('/backup')) return 2;
    return 0;
  };

  const [tab, setTab] = useState(getInitialTab());
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [settings, setSettings] = useState({
    twoFactor: false,
    passwordExpiry: true,
    sessionTimeout: true,
    ipWhitelist: false,
    auditLogging: true,
    loginAttemptLimit: true,
  });

  const filtered = mockAccessLogs.filter(log =>
    !search || `${log.user} ${log.email} ${log.action} ${log.ip}`.toLowerCase().includes(search.toLowerCase())
  );

  const failedCount = mockAccessLogs.filter(l => l.status === 'failed').length;

  const handleSave = () => setSnackbar({ open: true, message: 'Security settings saved', severity: 'success' });

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Security & Access</Typography>
        <Typography variant="body2" color="text.secondary">Manage security settings, monitor access, and configure backups</Typography>
      </Box>

      {/* Alert Banner */}
      {failedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <strong>{failedCount} failed login attempts</strong> detected in the last 48 hours. Review access logs below.
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab icon={<Security />} label="Security Settings" iconPosition="start" />
            <Tab icon={<History />} label={`Access Logs (${failedCount} alerts)`} iconPosition="start" />
            <Tab icon={<Backup />} label="Backup & Recovery" iconPosition="start" />
          </Tabs>
        </Box>

        {/* Security Settings Tab */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Security Policies</Typography>
                {[
                  { key: 'twoFactor', label: 'Two-Factor Authentication (2FA)', desc: 'Require 2FA for all admin and staff logins' },
                  { key: 'passwordExpiry', label: 'Password Expiration', desc: 'Force password reset every 90 days' },
                  { key: 'sessionTimeout', label: 'Session Timeout', desc: 'Auto-logout after 30 minutes of inactivity' },
                  { key: 'ipWhitelist', label: 'IP Allowlist', desc: 'Restrict admin access to approved IP addresses only' },
                  { key: 'auditLogging', label: 'Audit Logging', desc: 'Log all user actions and data changes' },
                  { key: 'loginAttemptLimit', label: 'Login Attempt Limit', desc: 'Lock accounts after 5 failed login attempts' },
                ].map((s, i) => (
                  <React.Fragment key={s.key}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Shield sx={{ fontSize: 16, color: settings[s.key] ? 'success.main' : 'text.disabled' }} />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{s.label}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>{s.desc}</Typography>
                      </Box>
                      <Switch checked={!!settings[s.key]} onChange={e => setSettings(p => ({ ...p, [s.key]: e.target.checked }))} color="primary" />
                    </Box>
                    {i < 5 && <Divider />}
                  </React.Fragment>
                ))}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" onClick={handleSave}>Save Security Settings</Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Security Alerts</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {securityAlerts.map((alert, i) => (
                    <Alert key={i} severity={alert.severity} sx={{ borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{alert.title}</Typography>
                      <Typography variant="body2">{alert.body}</Typography>
                      <Typography variant="caption" color="text.secondary">{alert.time}</Typography>
                    </Alert>
                  ))}
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Blocked IPs</Typography>
                  <Card elevation={1} sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No IPs currently blocked</Typography>
                      <TextField label="Block IP Address" fullWidth size="small" sx={{ mb: 1 }} placeholder="e.g. 203.0.113.47" />
                      <Button variant="outlined" color="error" fullWidth size="small" onClick={() => setSnackbar({ open: true, message: 'IP address blocked', severity: 'warning' })}>
                        Block IP
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Access Logs Tab */}
        {tab === 1 && (
          <Box>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <TextField
                placeholder="Search logs..."
                value={search} onChange={e => setSearch(e.target.value)}
                size="small" sx={{ minWidth: 280 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
              <Button startIcon={<Download />} variant="outlined" size="small">Export Logs</Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>IP Address</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map(log => (
                    <TableRow key={log.id} hover sx={{ bgcolor: log.status === 'failed' ? 'error.50' : 'transparent' }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{log.user}</Typography>
                        <Typography variant="caption" color="text.secondary">{log.email}</Typography>
                      </TableCell>
                      <TableCell>{log.action}</TableCell>
                      <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{log.ip}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{log.time}</Typography></TableCell>
                      <TableCell>
                        <Chip
                          label={log.status === 'success' ? 'Success' : 'Failed'}
                          color={log.status === 'success' ? 'success' : 'error'}
                          size="small"
                          icon={log.status === 'success' ? <CheckCircle sx={{ fontSize: '14px !important' }} /> : <Error sx={{ fontSize: '14px !important' }} />}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Backup & Recovery Tab */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Backup Configuration</Typography>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField label="Backup Location" defaultValue="Amazon S3 — us-east-1" fullWidth size="small" />
                    <TextField label="Backup Frequency" defaultValue="Daily at 2:00 AM" fullWidth size="small" />
                    <TextField label="Retention Period (days)" type="number" defaultValue="30" fullWidth size="small" />
                    <TextField label="Notification Email" type="email" defaultValue="admin@era.edu" fullWidth size="small" />
                    <FormControlLabel control={<Switch defaultChecked />} label="Email notification on success" />
                    <FormControlLabel control={<Switch defaultChecked />} label="Email notification on failure" />
                    <Button variant="contained" onClick={() => setSnackbar({ open: true, message: 'Backup settings saved', severity: 'success' })}>Save Configuration</Button>
                    <Button variant="outlined" onClick={() => setSnackbar({ open: true, message: 'Manual backup started — you will be notified when complete', severity: 'info' })}>
                      Run Manual Backup Now
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={7}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Backup History</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[
                        { date: '2024-01-18 02:00 AM', type: 'Automated', size: '124 MB', status: 'success' },
                        { date: '2024-01-17 02:00 AM', type: 'Automated', size: '123 MB', status: 'success' },
                        { date: '2024-01-16 02:00 AM', type: 'Automated', size: '122 MB', status: 'success' },
                        { date: '2024-01-15 03:15 PM', type: 'Manual', size: '122 MB', status: 'success' },
                        { date: '2024-01-15 02:00 AM', type: 'Automated', size: '121 MB', status: 'success' },
                        { date: '2024-01-14 02:00 AM', type: 'Automated', size: '120 MB', status: 'failed' },
                      ].map((b, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{b.date}</TableCell>
                          <TableCell><Chip label={b.type} size="small" variant="outlined" /></TableCell>
                          <TableCell>{b.size}</TableCell>
                          <TableCell><Chip label={b.status === 'success' ? 'Success' : 'Failed'} color={b.status === 'success' ? 'success' : 'error'} size="small" /></TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button size="small" variant="text" onClick={() => setSnackbar({ open: true, message: 'Backup downloaded', severity: 'success' })}>Download</Button>
                              {b.status === 'success' && (
                                <Button size="small" variant="text" color="warning" onClick={() => setSnackbar({ open: true, message: 'Restore initiated — this may take a few minutes', severity: 'warning' })}>Restore</Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SecurityAdmin;
